import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Checkbox, Input, Modal, Switch, type InputRef } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  getOrder,
  getPaymentOptions,
  setAutoPayment,
  startCardTopUp,
  startSBP,
  unlinkSavedCard,
  type PAOrder,
  type PAPaymentOptions,
  type PAQRCode,
  type PASavedCard,
} from '../api/payments';
import { redirectTo } from '../api/redirect';
import { formatInstant, formatMinor, parseMinor } from '../format';
import { Money } from '../money/Money';
import { FactList } from '../money/DataTable';
import { MoneyPage, QueryFrame } from '../money/MoneyPage';
import { usePAQuery } from '../money/usePAQuery';
import {
  orderFailureLabel,
  orderStateLabel,
  paymentsBlockedLabel,
  recommendedModeLabel,
  savedCardReasonLabel,
  savedCardStatusLabel,
} from '../money/labels';
import { errorText } from '../session/errorText';
import type { Navigation } from '../router';

/**
 * ============================================================================
 * «ОПЛАТА» — the screen this whole Personal Area exists for
 * ============================================================================
 *
 * W2 let a suspended subscriber READ the exact contract-wide figure that restores his
 * service. This is where he pays it: `ui-enduser-personal-area` §8b (the payment form),
 * §8d (the SBP QR and its wait for the bank), §10 (the proactive monthly auto-payment)
 * and §10a (the saved method and its unlink).
 *
 * # THE RULES OF THIS FILE, all four of which are properties somebody could break silently
 *
 *  1. **NO MONEY IS COMPUTED HERE.** The amount pre-filled is `prefill_amount`, which the
 *     SERVER selects — «for a suspended Account the recommended (and pre-filled) amount is
 *     the EXACT amount required to resume» (Appendix L §3) — and this screen never chooses
 *     between `resume_amount` and `recommended_amount` itself, never subtracts the balance
 *     from anything, and never scales a figure. The one conversion is `parseMinor`, which
 *     turns what the SUBSCRIBER typed back into minor units by concatenating his own
 *     digits. `src/money-arithmetic.test.ts` holds the rule with no exemption list.
 *  2. **NO CARD FIELD EXISTS.** A pan is typed on the acquirer's hosted page, reached
 *     through `redirect_url`. Nothing here accepts, renders, stores or logs a card number,
 *     a token or an anchor.
 *  3. **THE «ЗАПОМНИТЬ КАРТУ» CAPTION IS THE SERVER'S, RENDERED VERBATIM.** It is a
 *     REQUIREMENT with three named parts (`answers.md` P7-P1), not a label, and the box is
 *     PRE-TICKED from `save_card_default` — so the consent is given by NOT acting and the
 *     caption is the only text he is guaranteed to have seen. It is not shortened, not
 *     split across two elements, and not moved into a tooltip; when `save_card_available`
 *     is false the checkbox is not rendered at all, because a pre-ticked box promising a
 *     discount that provably cannot arrive is worse than no box.
 *  4. **A BUTTON THAT IS IN FLIGHT IS NOT PRESSABLE.** The backend decided what a double
 *     tap MEANS — two card taps make two independent orders and the balance still moves
 *     once, a second promise is a 409, unlink and the auto-payment switch are idempotent —
 *     and the screen's duty is not to re-decide it but to keep a subscriber on a phone
 *     from starting two acquirer forms he must then reconcile.
 *
 * # What is NOT on this screen, each named
 *
 *   - **«Подключить автоплатежи» as a checkbox on the form** (§8b lists one). The backend
 *     deliberately did not build it — it needs a column on `payonline_order` and a branch
 *     in the callback — and the toggle beside the saved card reaches the same state. A
 *     checkbox the server drops is a consent the subscriber believes he gave.
 *   - **an editable receipt contact.** The wire carries `receipt_email`/`receipt_phone` as
 *     a DISPLAY: `internal/fiscal` resolves the delivery contact from the subscriber's
 *     card at receipt time and `payonline.StartInput` has no contact field, so a box here
 *     would be a field nothing reads.
 *   - **a card RETURN page.** The acquirer's success and failure URLs land on the server's
 *     own `internal/payonline` landing page, which is a self-contained document with no
 *     external asset — reached by a subscriber who may have no internet beyond the garden.
 *     A return route in this SPA would be a second answer to a question that already has
 *     one, and the balance is what he comes back to read.
 *   - **multi-contract payment (§8c).** No backend, and enabling it would make
 *     auto-payments unavailable — a trade nobody has asked for.
 */
export function PaymentScreen({ navigate }: { navigate: Navigation['navigate'] }) {
  return (
    <MoneyPage titleKey="topup.title" leadKey="topup.lead">
      {(contract) => <PaymentBody contractID={contract.id} navigate={navigate} />}
    </MoneyPage>
  );
}

/** What the subscriber started, if anything: a card redirect or an SBP QR. */
type Started =
  | { kind: 'none' }
  | { kind: 'card'; paymentID: string; redirectURL: string }
  | { kind: 'sbp'; qr: PAQRCode };

function PaymentBody({
  contractID,
  navigate,
}: {
  contractID: number;
  navigate: Navigation['navigate'];
}) {
  const { query, reload } = usePAQuery<PAPaymentOptions>(
    () => getPaymentOptions(contractID),
    [contractID],
  );

  return (
    <QueryFrame query={query} reload={reload}>
      {(options) => (
        <PaymentForm
          key={options.contract_id}
          options={options}
          reload={reload}
          navigate={navigate}
        />
      )}
    </QueryFrame>
  );
}

function PaymentForm({
  options,
  reload,
  navigate,
}: {
  options: PAPaymentOptions;
  reload: () => void;
  navigate: Navigation['navigate'];
}) {
  const { t } = useTranslation();
  /*
    THE AMOUNT LIVES AS THE TEXT HE TYPED, not as a number.

    ⚠ It is seeded from `prefill_amount` through `formatMinor`, and that round trip is
    exact by construction (`parseMinor(formatMinor(n)) === n` for every safe integer,
    pinned in `format.test.ts`) — so a subscriber who touches nothing pays the engine's
    own figure to the kopeck. Holding a NUMBER of rubles here instead would mean a
    multiplication by 100 on submit, which is arithmetic on money and the very thing this
    app forbids; it would also lose «450,00» → 450 → «450», and a figure that changes
    shape while he looks at it is a figure he distrusts.
  */
  const [amountText, setAmountText] = useState(() => formatMinor(options.prefill_amount));
  const [saveCard, setSaveCard] = useState(options.save_card_default);
  const [busy, setBusy] = useState<'' | 'card' | 'sbp'>('');
  const [failure, setFailure] = useState<unknown>(null);
  const [localError, setLocalError] = useState('');
  const [started, setStarted] = useState<Started>({ kind: 'none' });
  const amountRef = useRef<InputRef>(null);

  /*
    ⚠ THE PREFILL FOLLOWS THE SERVER'S FIGURE WHEN IT MOVES, and only while the field is
    untouched. A subscriber who leaves the page open while a payment lands, or who
    switches contract, must not be offered a stale resume amount — that is the D64/D74
    shape one layer up: a correct figure, printed after it stopped being true. Editing the
    box marks it his, and nothing overwrites it afterwards.
  */
  const [pristine, setPristine] = useState(true);
  useEffect(() => {
    if (pristine) setAmountText(formatMinor(options.prefill_amount));
  }, [options.prefill_amount, pristine]);

  const blocked = paymentsBlockedLabel(options.payments_blocked_reason, t);
  if (!options.payments_allowed) {
    return (
      <>
        <BalanceCard options={options} />
        <Alert
          className="pa-block"
          type="warning"
          showIcon
          message={t('topup.blockedTitle')}
          description={blocked}
        />
        {/* ⚠ THE SAVED CARD IS RENDERED HERE TOO, and that is not symmetry for its own
            sake. `answers.md`:28 and §10a both say the Subscriber may unlink «at any
            moment», and both states that reach this branch are states in which he may most
            want to: an acquirer switched off after he saved a card, or an operator who has
            just closed portal payments to legal entities. Returning early would take the
            unlink lever — and the auto-payment switch — away from a Contract that still has
            an active recurring method the system can charge. What he loses here is the
            FORM, which is what «payments are not offered» means; not the management of a
            consent he already gave. */}
        <SavedCardCard card={options.saved_card} reload={reload} contractID={options.contract_id} />
        <OtherMethodsLink navigate={navigate} />
      </>
    );
  }

  /** submit is the ONE body of both instruments — the checks are identical. */
  const submit = async (instrument: 'card' | 'sbp') => {
    setFailure(null);
    setLocalError('');
    const value = parseMinor(amountText);
    if (value === null || value <= 0) {
      setLocalError(t('topup.amountInvalid'));
      amountRef.current?.focus();
      return;
    }
    // The instance's minimum, refused LOCALLY with the same figure the server refuses
    // with — `min_amount` is on the wire for exactly this, so a subscriber learns it
    // before a round trip rather than as a 409 after one. The server checks it again;
    // this is a courtesy, not the enforcement.
    if (options.min_amount > 0 && value < options.min_amount) {
      setLocalError(t('topup.amountTooSmall', { min: formatMinor(options.min_amount) }));
      amountRef.current?.focus();
      return;
    }
    setBusy(instrument);
    try {
      if (instrument === 'sbp') {
        const qr = await startSBP(options.contract_id, value);
        setStarted({ kind: 'sbp', qr });
      } else {
        const topup = await startCardTopUp(options.contract_id, value, saveCard);
        setStarted({ kind: 'card', paymentID: topup.payment_id, redirectURL: topup.redirect_url });
        // The acquirer's hosted form IS the next step; the panel below is the fallback
        // for a browser that refuses the programmatic navigation.
        redirectTo(topup.redirect_url);
      }
    } catch (err) {
      // ⚠ THE SERVER'S OWN SENTENCE, whatever it is — 503 «оплата картой недоступна», 409
      // «below the minimum», 403 «not offered to legal entities». `errorText` is the one
      // place a failure becomes text, and a second catalogue here would drift from the
      // one that carries the meaning.
      setFailure(err);
    } finally {
      setBusy('');
    }
  };

  return (
    <>
      <BalanceCard options={options} />

      {started.kind === 'card' ? (
        <Alert
          className="pa-block"
          type="info"
          showIcon
          message={t('topup.redirectTitle')}
          description={
            <>
              <p className="pa-notice-note">{t('topup.redirectText')}</p>
              {/* An ORDINARY LINK, so a long press can copy it and a blocked programmatic
                  navigation is not a dead end. The URL is the server's, whole. */}
              <a href={started.redirectURL} rel="noopener noreferrer">
                {t('topup.redirectLink')}
              </a>
            </>
          }
        />
      ) : null}

      {started.kind === 'sbp' ? (
        <SBPPanel
          qr={started.qr}
          currency={options.currency}
          onRestart={() => setStarted({ kind: 'none' })}
        />
      ) : null}

      <Card className="pa-block" title={t('topup.title')}>
        {failure ? (
          <Alert
            type="error"
            showIcon
            message={errorText(failure, t)}
            style={{ marginBottom: 16 }}
          />
        ) : null}

        <label className="pa-field-label" htmlFor="pa-amount">
          {t('topup.amountLabel')}
        </label>
        <Input
          id="pa-amount"
          ref={amountRef}
          className="pa-amount-input"
          size="large"
          // `inputMode="decimal"` gives a phone the numeric keypad with a separator, and
          // `type="text"` keeps the ru-RU comma typable — `type="number"` rejects it in
          // several mobile browsers, which is how «450,00» becomes an empty box.
          inputMode="decimal"
          autoComplete="off"
          value={amountText}
          placeholder={t('topup.amountPlaceholder')}
          onChange={(e) => {
            setPristine(false);
            setLocalError('');
            setAmountText(e.target.value);
          }}
          status={localError ? 'error' : undefined}
        />
        {localError ? <p className="pa-field-error">{localError}</p> : null}
        <p className="pa-field-hint">
          {options.resume_amount > 0
            ? t('topup.prefillResume')
            : `${t('topup.prefillRecommended')} ${recommendedModeLabel(options.recommended_mode, t)}`}
        </p>

        {options.card_enabled && options.save_card_available ? (
          <div className="pa-consent">
            <Checkbox
              checked={saveCard}
              onChange={(e) => setSaveCard(e.target.checked)}
              data-testid="pa-save-card"
            >
              {/* ⚠ VERBATIM, AS ONE ELEMENT. See rule 3 in this file's header: the caption
                  is the requirement, the server is the only place it can be tested, and
                  splitting it or trimming it here would silently delete a consent. */}
              {options.save_card_caption}
            </Checkbox>
          </div>
        ) : null}

        <div className="pa-actions">
          {options.card_enabled ? (
            <Button
              type="primary"
              size="large"
              block
              loading={busy === 'card'}
              onClick={() => void submit('card')}
            >
              {t('topup.payCard')}
            </Button>
          ) : null}
          {options.sbp_enabled ? (
            <Button size="large" block loading={busy === 'sbp'} onClick={() => void submit('sbp')}>
              {t('topup.paySBP')}
            </Button>
          ) : null}
        </div>

        <p className="pa-field-hint">{t('topup.cardOnAcquirer')}</p>
        <ReceiptContacts options={options} />
      </Card>

      <SavedCardCard
        card={options.saved_card}
        reload={reload}
        contractID={options.contract_id}
        onResave={() => {
          // §10a's «re-save» shortcut: the form with «Запомнить карту» ticked. It is the
          // same form rather than a second one, because a second form is a second place
          // for the caption to be wrong.
          setSaveCard(true);
          amountRef.current?.focus();
          amountRef.current?.input?.scrollIntoView?.({ block: 'center' });
        }}
      />

      <OtherMethodsLink navigate={navigate} />
    </>
  );
}

/** The balance and, when there is one, the resume figure the prefill came from. */
function BalanceCard({ options }: { options: PAPaymentOptions }) {
  const { t } = useTranslation();
  return (
    <Card className="pa-block">
      <FactList
        facts={[
          {
            key: 'balance',
            label: t('topup.balance'),
            value: <Money minor={options.balance} currency={options.currency} strong colored />,
          },
        ]}
      />
    </Card>
  );
}

/** §8b's receipt block — a DISPLAY. Rendered only when a contact exists to show. */
function ReceiptContacts({ options }: { options: PAPaymentOptions }) {
  const { t } = useTranslation();
  if (!options.receipt_email && !options.receipt_phone) return null;
  const facts = [];
  if (options.receipt_email) {
    facts.push({ key: 'email', label: t('topup.receiptEmail'), value: options.receipt_email });
  }
  if (options.receipt_phone) {
    facts.push({ key: 'phone', label: t('topup.receiptPhone'), value: options.receipt_phone });
  }
  return (
    <div className="pa-subblock">
      <p className="pa-section-subtitle">{t('topup.receiptTitle')}</p>
      <FactList facts={facts} />
      <p className="pa-field-hint">{t('topup.receiptHint')}</p>
    </div>
  );
}

/**
 * §8d — the QR, the link, and the wait for the bank's own confirmation.
 *
 * ⚠ THE IMAGE IS A `data:` URI AND THE ONLY IMAGE IN THIS APP. It is the gateway's own
 * rendering, returned inline and never stored — `internal/payonline` records that rule for
 * the server and it holds here too: it lives in component state and disappears with the
 * panel. A remote image would be unreachable for exactly the subscriber this screen exists
 * for, which is why `src/bundle.test.ts` forbids one; a data URI fetches nothing.
 *
 * ⚠ THE PAYLOAD LINK IS THE ONE OUTWARD JOURNEY THIS SCREEN OFFERS, and it is honest about
 * being one: it opens a BANK APP by scheme, which the walled garden does not mediate,
 * whereas anything the browser would have to FETCH is not offered at all.
 */
function SBPPanel({
  qr,
  currency,
  onRestart,
}: {
  qr: PAQRCode;
  currency: string;
  onRestart: () => void;
}) {
  const { t } = useTranslation();
  const [order, setOrder] = useState<PAOrder | null>(null);
  const [checking, setChecking] = useState(false);

  const poll = async () => {
    setChecking(true);
    try {
      setOrder(await getOrder(qr.payment_id));
    } catch {
      // A failed poll is not a failed payment, and saying so would be worse than saying
      // nothing: the state on screen stays «ожидание подтверждения банка», which is what
      // is actually known. The callback is what settles the order, not this read.
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    // A slow poll while the panel is open, cleaned up on unmount. It is deliberately not
    // fast: the authenticated server-to-server callback is what settles the order (S17),
    // and this read only notices that it happened.
    const settled = order?.state === 'paid' || order?.state === 'failed' || order?.state === 'expired';
    if (settled) return undefined;
    const timer = window.setTimeout(() => void poll(), 5000);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, qr.payment_id]);

  const state = order?.state ?? 'pending';
  return (
    <Card className="pa-block" title={t('topup.sbp.title')}>
      <FactList
        facts={[
          {
            key: 'amount',
            label: t('topup.sbp.amount'),
            value: <Money minor={qr.amount} currency={currency} strong />,
          },
          {
            key: 'state',
            label: t('topup.order.stateLabel'),
            value: <span className="pa-status">{orderStateLabel(state, t)}</span>,
          },
        ]}
      />

      {state === 'pending' ? (
        <>
          <p className="pa-field-hint">{t('topup.sbp.instructions')}</p>
          {qr.image_base64 ? (
            <img
              className="pa-qr"
              alt={t('topup.sbp.qrAlt')}
              src={`data:image/png;base64,${qr.image_base64}`}
            />
          ) : null}
          {qr.payload ? (
            <p>
              <a href={qr.payload} rel="noopener noreferrer">
                {t('topup.sbp.openLink')}
              </a>
            </p>
          ) : null}
          <p className="pa-field-hint">
            {t('topup.sbp.expires', { when: formatInstant(qr.expires_at) })}
          </p>
          <p className="pa-field-hint">{t('topup.order.pendingText')}</p>
          <Button loading={checking} onClick={() => void poll()}>
            {t('topup.sbp.refresh')}
          </Button>
        </>
      ) : null}

      {state === 'paid' ? (
        <Alert type="success" showIcon message={t('topup.order.paidText')} />
      ) : null}
      {state === 'failed' ? (
        <Alert
          type="error"
          showIcon
          message={orderFailureLabel(order?.failure_code ?? '', order?.failure_reason ?? '', t)}
        />
      ) : null}
      {state === 'expired' ? (
        <Alert type="warning" showIcon message={t('topup.order.expiredText')} />
      ) : null}

      {state !== 'pending' ? <Button onClick={onRestart}>{t('topup.sbp.restart')}</Button> : null}
      <p className="pa-field-hint">{t('topup.sbp.note')}</p>
    </Card>
  );
}

/**
 * §10a + §10 — the saved method, its unlink, and the proactive monthly switch.
 *
 * ⚠ THE TWO CONTROLS ARE DIFFERENT CONSENTS AND THE SCREEN SAYS SO. Turning the switch off
 * stops the PROACTIVE monthly charge and nothing else — the card stays, the failed-boundary
 * fallback and the dunning attempts keep running, the recurring-method discount is
 * unaffected. Unlinking does all three. Before migration 0131 there was no way to express
 * the difference, so the only lever a subscriber had switched off three consents at once;
 * rendering them as one control again would restore that.
 */
function SavedCardCard({
  card,
  contractID,
  reload,
  onResave,
}: {
  card: PASavedCard | null;
  contractID: number;
  reload: () => void;
  /**
   * The §10a re-save shortcut — ABSENT when there is no form to send him to, which is the
   * state an installation with no usable acquirer is in. The prompt still explains why the
   * card stopped working; what it does not do is offer a button that would land on the
   * refusal panel he is already reading. A control that cannot act is the D109 class.
   */
  onResave?: () => void;
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  if (!card) {
    return (
      <Card className="pa-block" data-testid="pa-saved-card" title={t('topup.savedCard.title')}>
        <p className="pa-field-hint">{t('topup.savedCard.none')}</p>
      </Card>
    );
  }

  const expired = card.status === 'expired';

  const unlink = async () => {
    setBusy(true);
    setFailure(null);
    try {
      await unlinkSavedCard(contractID);
      setConfirming(false);
      // The options read is what owns this card's state, so the screen re-reads rather
      // than patching a local copy — a local patch is how two views of one card start
      // disagreeing.
      reload();
    } catch (err) {
      setFailure(err);
    } finally {
      setBusy(false);
    }
  };

  const toggleAutoPay = async (next: boolean) => {
    setBusy(true);
    setFailure(null);
    try {
      await setAutoPayment(contractID, next);
      reload();
    } catch (err) {
      setFailure(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    // The test hook is deliberate: the block's heading also occurs inside the pre-ticked
    // consent caption, so «find the saved-card block by its title» is ambiguous by
    // construction and an arm that used it would be asserting on whichever element came
    // first.
    <Card className="pa-block" data-testid="pa-saved-card" title={t('topup.savedCard.title')}>
      {failure ? (
        <Alert type="error" showIcon message={errorText(failure, t)} style={{ marginBottom: 16 }} />
      ) : null}

      <FactList facts={savedCardFacts(card, t)} />

      {expired ? (
        <Alert
          className="pa-block"
          type="warning"
          showIcon
          message={t('topup.savedCard.expiredTitle')}
          description={
            <>
              <p className="pa-notice-note">{t('topup.savedCard.expiredText')}</p>
              {onResave ? (
                <Button type="primary" onClick={onResave}>
                  {t('topup.savedCard.expiredAction')}
                </Button>
              ) : null}
            </>
          }
        />
      ) : null}

      {!expired && !card.chargeable ? (
        <Alert type="warning" showIcon message={t('topup.savedCard.notChargeable')} />
      ) : null}

      {!expired ? (
        <div className="pa-consent">
          <label className="pa-switch-row">
            <Switch
              checked={card.auto_pay_enabled}
              loading={busy}
              onChange={(next) => void toggleAutoPay(next)}
            />
            <span>{t('topup.autoPay.label')}</span>
          </label>
          <p className="pa-field-hint">{t('topup.autoPay.hint')}</p>
          <p className="pa-field-hint">{t('topup.autoPay.separation')}</p>
        </div>
      ) : null}

      <div className="pa-actions">
        <Button danger onClick={() => setConfirming(true)}>
          {t('topup.savedCard.unlink')}
        </Button>
      </div>

      {/* §10a's confirmation dialog, with the three warnings it requires BEFORE the
          subscriber confirms — plus the discount, which is the fourth thing that stops
          and the one he is least likely to expect. */}
      <Modal
        open={confirming}
        title={t('topup.savedCard.unlinkTitle')}
        okText={t('topup.savedCard.unlinkConfirm')}
        cancelText={t('topup.savedCard.unlinkCancel')}
        okButtonProps={{ danger: true, loading: busy }}
        onOk={() => void unlink()}
        onCancel={() => setConfirming(false)}
      >
        <ul className="pa-warnings">
          <li>{t('topup.savedCard.unlinkWarnAuto')}</li>
          <li>{t('topup.savedCard.unlinkWarnManual')}</li>
          <li>{t('topup.savedCard.unlinkWarnAnchor')}</li>
          <li>{t('topup.savedCard.unlinkWarnDiscount')}</li>
        </ul>
      </Modal>
    </Card>
  );
}

function savedCardFacts(card: PASavedCard, t: TFunction) {
  const facts = [
    {
      key: 'pan',
      // ⚠ NOT `savedCard.title`, which is the BLOCK's own heading AND appears inside the
      // «Запомнить карту» caption («Оплата → Сохранённый способ оплаты»). Three elements
      // carrying one wording make every `getByText` in the suite ambiguous, which is how a
      // test starts asserting on the wrong element — a defect this app has already paid
      // for once.
      label: t('topup.savedCard.pan'),
      // The acquirer's own mask, printed as given. Nothing here reformats it: a mask this
      // app re-derived would be a card number this app had touched.
      value: <span className="mono">{card.masked_pan}</span>,
    },
  ];
  if (card.card_holder) {
    facts.push({ key: 'holder', label: t('topup.savedCard.holder'), value: <>{card.card_holder}</> });
  }
  facts.push(
    { key: 'acquirer', label: t('topup.savedCard.acquirer'), value: <>{card.acquirer}</> },
    { key: 'saved', label: t('topup.savedCard.savedAt'), value: <>{formatInstant(card.saved_at)}</> },
    {
      key: 'status',
      label: t('topup.savedCard.statusLabel'),
      value: (
        <span className={`pa-status pa-status-${card.status === 'active' ? 'ok' : 'bad'}`}>
          {savedCardStatusLabel(card.status, t)}
        </span>
      ),
    },
  );
  if (card.expired_reason) {
    facts.push({
      key: 'reason',
      label: t('topup.savedCard.reasonLabel'),
      value: <>{savedCardReasonLabel(card.expired_reason, t)}</>,
    });
  }
  return facts;
}

/** §8e's secondary link — «one click from the alternative rather than one phone call». */
function OtherMethodsLink({ navigate }: { navigate: Navigation['navigate'] }) {
  const { t } = useTranslation();
  return (
    <div className="pa-form-footer">
      <Button type="link" style={{ padding: 0 }} onClick={() => navigate({ kind: 'instructions' })}>
        {t('topup.otherMethods')}
      </Button>
      <p className="pa-field-hint">{t('topup.otherMethodsHint')}</p>
    </div>
  );
}
